import { Alert } from '@unmind/design-system';
import { AddToCalendar } from 'Shared/AddToCalendar';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { rem } from 'polished';
import styled from 'styled-components';
import { Namespace } from 'i18next';
import RoutePath from 'App/RoutePath';
import BackButton from 'Shared/Button/BackButton';
import { getChannel } from 'utils';
import {
  HeaderContainer,
  TitleAndSubtitle,
  Title,
  Description,
  AddToCalendarContainer,
} from './styles';

interface CourseHeaderProps {
  title: string;
  subtitle: string;
  isNotAnonymised: boolean;
}

const StyledAlert = styled(Alert)`
  position: relative;
  margin: 0 auto ${rem(24)} auto;
  padding-left: ${rem(100)};
  align-items: start;
  box-sizing: border-box;

  > svg {
    position: absolute;
    top: ${rem(40)};
    left: ${rem(40)};
  }

  span {
    text-align: left;
  }
`;

export const CourseHeader = ({
  title,
  subtitle,
  isNotAnonymised,
}: CourseHeaderProps) => {
  const { t: translate } = useTranslation<Namespace<'courses'>>('courses');
  const { supportUrl } = getChannel();

  return (
    <>
      <HeaderContainer>
        {/*
          Return the user to wherever they opened the course from (Home,
          Managers, an Explore category, the Wellbeing Tracker, etc.).
          `BackButton` uses browser history and only falls back to
          `linkToDefault` when the course was the entry route (e.g. a deep link
          or hard refresh, where there is no history to return to).
        */}
        <BackButton linkToDefault={RoutePath.Explore} />
        <TitleAndSubtitle>
          <Title>{title}</Title>
          <Description>{subtitle}</Description>
        </TitleAndSubtitle>
        <AddToCalendarContainer>
          <AddToCalendar
            title={title}
            useSmallButton={true}
            description={subtitle}
            buttonTextKey={'add_to_calendar.course_reminder_text'}
            contentPath={window.location.pathname}
          />
        </AddToCalendarContainer>
      </HeaderContainer>
      {isNotAnonymised ? (
        <div data-testid="alert-disclaimer">
          <StyledAlert
            variant="info"
            size="medium"
            orientation="vertical"
            bodyContainsMarkup={true}
            title={translate('overview.tracking_alert_disclaimer.title', {
              series_title: title,
            })}
            children={Trans({
              t: translate,
              i18nKey: 'overview.tracking_alert_disclaimer.text',
              defaults:
                "However, we understand the importance of privacy and it's something we take seriously at {{brandName}}. We want to reassure you that the rest of your platform usage remains completely anonymised and confidential. If you have any concerns, please <support_link>reach out to our support team</support_link>.",
              components: {
                support_link: (
                  // eslint-disable-next-line jsx-a11y/anchor-has-content
                  <a
                    href={supportUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  />
                ),
              },
            })}
          />
        </div>
      ) : null}
    </>
  );
};
